'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import { Typography } from '../Typography/Typography';
import { cn } from '@/app/lib';
import { isDisabled } from '@/app/lib/common/DropdownFilter/dropdownFilter.util';
import { DropdownOption } from '../Dropdowns';
import { IconMap } from '../IconMap';
import { CustomScrollBar } from '../CustomScrollBar';

export type FilterMultiSelectProps = {
  placeholder: string;
  startIcon?: keyof typeof IconMap | '';
  icon: keyof typeof IconMap;
  endIcon?: keyof typeof IconMap | '';
  options: DropdownOption[];
  onSelect?: (values: string[]) => void;
  value?: string[];
  syncExternalValue?: boolean;
  className?: string;
  iconClassName?: string;
  optionClassName?: string;
  optionIconClassName?: string;
  iconSize?: number;
  emptyState?: React.ReactNode;
  multiSelect?: boolean;
  showCheckbox?: boolean;
  maxDisplayCount?: number;
  fullWidth?: boolean;
  disabled?: boolean;
};

const DefaultEmptyState = (
  <div className="p-4">
    <Typography variant="bodyMd" className="text-center">
      No options available
    </Typography>
  </div>
);

export function FilterMultiSelect({
  placeholder,
  startIcon,
  icon = 'Filter',
  endIcon,
  options,
  onSelect,
  value,
  syncExternalValue,
  className,
  iconClassName = 'text-secondary-400',
  optionClassName,
  optionIconClassName,
  iconSize = 24,
  emptyState = DefaultEmptyState,
  multiSelect = false,
  showCheckbox = false,
  maxDisplayCount = 2,
  fullWidth = false,
  disabled = false,
}: FilterMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => {
    if (!disabled) {
      setIsOpen((v) => !v);
    }
  }, [disabled]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      if (multiSelect) {
        const newValues = selectedValues.includes(optionValue)
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue];
        setSelectedValues(newValues);
        onSelect?.(newValues);
      } else {
        const newValues = [optionValue];
        setSelectedValues(newValues);
        onSelect?.(newValues);
        setIsOpen(false);
      }
    },
    [multiSelect, selectedValues, onSelect],
  );

  const isSelected = useCallback((optionValue: string) => selectedValues.includes(optionValue), [selectedValues]);

  const getDisplayText = useCallback(() => {
    if (selectedValues.length === 0) {
      return placeholder;
    }

    if (!multiSelect) {
      const selectedOption = options.find((o) => o.value === selectedValues[0]);
      return selectedOption?.label ?? placeholder;
    }

    if (selectedValues.length <= maxDisplayCount) {
      return selectedValues
        .map((v) => options.find((o) => o.value === v)?.label)
        .filter(Boolean)
        .join(', ');
    }

    return `${selectedValues.length} selected`;
  }, [selectedValues, placeholder, multiSelect, options, maxDisplayCount]);

  // outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [close]);

  // focus the list when opened
  useEffect(() => {
    if (isOpen) {
      // next microtask avoids race with mount
      queueMicrotask(() => listRef.current?.focus());
    }
  }, [isOpen]);

  // Sync external value with internal selected state
  useEffect(() => {
    if (!syncExternalValue) return;
    if (value && Array.isArray(value)) {
      // Only sync values that exist in current options
      const availableValues = options.map((o) => o.value);
      const validValues = value.filter((v) => availableValues.includes(v));
      setSelectedValues(validValues);
    } else if (!value) {
      setSelectedValues([]);
    }
  }, [value, syncExternalValue, options]);

  return (
    <div ref={rootRef} className={cn('relative w-full', className, fullWidth ? '' : 'md:max-w-50')}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={toggle}
        disabled={disabled}
        className={cn(
          'flex h-12 w-full items-center gap-2 rounded-sm transition-all duration-200 border border-secondary-100 bg-white px-3 cursor-pointer hover:bg-secondary-50 focus:ring-2 focus:ring-primary focus:rounded-[6px] focus:shadow-none outline-none focus:ring-offset-3',
          isOpen && 'ring-2 ring-primary rounded-[6px] ring-offset-3',
          disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
        )}
      >
        <Typography variant="bodyMd" className={'font-body text-secondary-200 line-clamp-1 text-left'}>
          {getDisplayText()}
        </Typography>
        <span className="ml-auto">
          <Icon name={icon} size={iconSize} className={iconClassName} />
        </span>
      </button>

      {isOpen && (
        <div className={'absolute z-10 mt-2 w-full rounded-[1px] border border-secondary-100 bg-white shadow-lg'}>
          {options.length > 0 ? (
            <CustomScrollBar
              externalScrollRef={listRef as unknown as React.RefObject<HTMLDivElement>}
              maxHeight="max-h-40"
              className="w-full"
              trackInsetTopPx={10}
              trackInsetBottomPx={10}
              contentClassName="py-1 pr-1"
              trackClassName="right-0"
              thumbWidth="w-[3px]"
              thumbHeight="!h-22"
              thumbClassName="bg-primary"
              autoHide={false}
            >
              <ul role="listbox" tabIndex={0} aria-label={placeholder} className="w-full">
                {options.map((o) => (
                  <li
                    key={o.id}
                    className={cn(
                      'border-b border-accent last:border-0 hover:border-b hover:border-primary hover:!cursor-pointer',
                      isSelected(o.value) ? 'bg-secondary-50' : '',
                      isDisabled(o) && 'border-b bg-secondary-50 hover:border-none pointer-events-none',
                    )}
                  >
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected(o.value)}
                      disabled={isDisabled(o)}
                      onClick={() => !isDisabled(o) && handleSelect(o.value)}
                      className={cn(
                        'flex w-full items-center gap-2 py-2 px-4 text-left hover:!cursor-pointer',
                        optionClassName,
                      )}
                    >
                      {multiSelect && showCheckbox && (
                        <input
                          type="checkbox"
                          checked={isSelected(o.value)}
                          readOnly
                          className="h-4 w-4 rounded border-secondary-100"
                          tabIndex={-1}
                        />
                      )}
                      {startIcon && <Icon name={startIcon} size={iconSize} className={optionIconClassName} />}
                      <Typography variant="bodySm" className="text-gray-700 flex-1">
                        {o.label}
                      </Typography>
                      {endIcon && <Icon name={endIcon} size={iconSize} className={optionIconClassName} />}
                    </button>
                  </li>
                ))}
              </ul>
            </CustomScrollBar>
          ) : (
            <>{emptyState}</>
          )}
        </div>
      )}
    </div>
  );
}

export default FilterMultiSelect;
